import {
  ArrowDown,
  ArrowUpRight,
  BookOpen,
  Church,
  Lightbulb,
  MessageCircleMore,
  Network,
  Rocket,
  Smartphone,
  Sparkles,
  Target,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

type Strategy = {
  number: string;
  verb: string;
  english: string;
  description: string;
  icon: typeof Target;
};

type Generation = {
  number: string;
  title: string;
  kicker: string;
  description: string;
  image: string;
  alt: string;
};

const strategies: Strategy[] = [
  {
    number: "01",
    verb: "Alcanzar",
    english: "Reaching",
    description:
      "Compartimos el Evangelio de Jesucristo con las nuevas generaciones mediante lenguajes, espacios y métodos innovadores que conectan con su realidad.",
    icon: Target,
  },
  {
    number: "02",
    verb: "Plantar",
    english: "Planting",
    description:
      "Impulsamos iglesias y comunidades de fe saludables, acogedoras y relevantes donde cada generación encuentre un lugar para crecer y servir.",
    icon: Church,
  },
  {
    number: "03",
    verb: "Multiplicar",
    english: "Multiplying",
    description:
      "Formamos líderes, multiplicamos comunidades y expandimos la obra misionera para que el Evangelio continúe avanzando de generación en generación.",
    icon: Network,
  },
];

const generations: Generation[] = [
  {
    number: "01",
    title: "Niños",
    kicker: "Una fe que echa raíces",
    description:
      "Acompañamos a iglesias y familias para que cada niño conozca a Jesús en ambientes seguros, alegres, bíblicos y llenos de propósito.",
    image: assetUrl("children_ministry_group.png"),
    alt: "Niños compartiendo y aprendiendo juntos en una comunidad de fe",
  },
  {
    number: "02",
    title: "Jóvenes",
    kicker: "Una fe que encuentra propósito",
    description:
      "Creamos espacios de encuentro, discipulado, adoración y servicio donde los jóvenes desarrollan sus dones y viven una fe auténtica.",
    image: assetUrl("youth_ministry_worship.png"),
    alt: "Jóvenes participando en una celebración de adoración",
  },
  {
    number: "03",
    title: "Jóvenes adultos",
    kicker: "Una fe que toma su lugar",
    description:
      "Equipamos a una generación para liderar, servir, asumir su vocación y llevar la esperanza de Jesús a cada espacio de la sociedad.",
    image: assetUrl("young_adults_ministry.png"),
    alt: "Jóvenes adultos reunidos en comunidad",
  },
];

const focus = [
  {
    number: "01",
    title: "Conectar",
    description:
      "Nos acercamos a las nuevas generaciones por medio de la tecnología, las redes sociales y los canales que forman parte de su vida cotidiana.",
    icon: Smartphone,
  },
  {
    number: "02",
    title: "Equipar",
    description:
      "Ofrecemos herramientas, relaciones y recursos para vivir una fe firme, descubrir el llamado de Dios y servir con excelencia.",
    icon: BookOpen,
  },
  {
    number: "03",
    title: "Desafiar",
    description:
      "Invitamos a niños y jóvenes a levantarse como agentes de cambio y transformación en su iglesia, comunidad y mundo.",
    icon: Rocket,
  },
];

const keywords = [
  {
    title: "Innovación",
    description: "Nuevas formas de presentar el Evangelio y hacer la misión.",
    icon: Lightbulb,
  },
  {
    title: "Relevancia",
    description: "Una iglesia presente, cercana y comprensible para cada generación.",
    icon: MessageCircleMore,
  },
  {
    title: "Multiplicación",
    description: "Líderes que forman líderes y comunidades que plantan comunidades.",
    icon: Sparkles,
  },
];

export default function NextGenPage() {
  return (
    <div className="acad-react-page acad-ng-page">
      <Header />

      <main>
        <section className="acad-ng-hero" data-nav="dark">
          <img
            data-acad-edit-id="nextgen-hero"
            data-acad-edit-label="Hero de Nuevas Generaciones"
            className="acad-ng-hero-image"
            src={assetUrl("youth_ministry_worship.png")}
            alt="Nueva generación adorando y creciendo en comunidad"
            loading="eager"
            decoding="async"
          />
          <span className="acad-ng-hero-shade" />
          <span className="acad-ng-hero-grid" aria-hidden="true" />

          <div className="acad-ng-shell acad-ng-hero-inner">
            <div className="acad-ng-hero-topline">
              <span className="acad-ng-badge">MM33</span>
              <span>Pilar Nuevas Generaciones · Costa Rica</span>
            </div>

            <div className="acad-ng-hero-copy">
              <span className="acad-ng-eyebrow acad-ng-eyebrow--light">
                Una fe que continúa
              </span>
              <h1>
                Nuevas
                <span>Generaciones</span>
              </h1>
              <p>
                El mensaje no cambia. <strong>La manera de alcanzarlos sí.</strong>
              </p>
            </div>

            <div className="acad-ng-hero-bottom">
              <a className="acad-ng-button" href="#estrategia">
                Conocer la estrategia <ArrowDown aria-hidden="true" />
              </a>
              <div className="acad-ng-hero-audience" aria-label="Personas a quienes servimos">
                <span>Niños</span>
                <span>Jóvenes</span>
                <span>Jóvenes adultos</span>
              </div>
            </div>
          </div>

          <span className="acad-ng-hero-word" aria-hidden="true">NEXT</span>
        </section>

        <div className="acad-ng-marquee" aria-label="Visión estratégica">
          <div>
            <span>Alcanzar</span><i />
            <span>Plantar</span><i />
            <span>Multiplicar</span><i />
            <span>Alcanzar</span><i />
            <span>Plantar</span><i />
            <span>Multiplicar</span><i />
          </div>
        </div>

        <section className="acad-ng-vision" id="vision">
          <div className="acad-ng-shell acad-ng-vision-grid">
            <div className="acad-ng-vision-heading">
              <span className="acad-ng-eyebrow">La iglesia de hoy</span>
              <h2>Una fe que continúa de generación en generación.</h2>
            </div>
            <div className="acad-ng-vision-copy">
              <p>
                Damos prioridad a conectar con niños y jóvenes para asegurar la continuidad de
                la fe y construir comunidades saludables, vivas y preparadas para el futuro.
              </p>
              <p>
                Adaptamos nuestros métodos de evangelización sin cambiar el centro del mensaje:
                <strong> Jesucristo sigue siendo la esperanza para cada generación.</strong>
              </p>
              <blockquote>
                “Generación a generación celebrará tus obras y anunciará tus hechos poderosos.”
                <cite>Salmo 145:4</cite>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="acad-ng-strategy" id="estrategia">
          <div className="acad-ng-shell">
            <header className="acad-ng-section-head acad-ng-section-head--dark">
              <span className="acad-ng-eyebrow acad-ng-eyebrow--light">Objetivos estratégicos</span>
              <div>
                <h2>Tres movimientos.<br />Una misión.</h2>
                <p>
                  Reaching, Planting and Multiplying: una estrategia para llevar el Evangelio,
                  establecer comunidades y activar líderes que multipliquen la misión.
                </p>
              </div>
            </header>

            <div className="acad-ng-strategy-grid">
              {strategies.map((strategy) => {
                const Icon = strategy.icon;

                return (
                  <article className="acad-ng-strategy-card" key={strategy.number}>
                    <div className="acad-ng-strategy-top">
                      <span>{strategy.number}</span>
                      <i><Icon aria-hidden="true" /></i>
                    </div>
                    <small>{strategy.english}</small>
                    <h3>{strategy.verb}</h3>
                    <p>{strategy.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-ng-generations" id="generaciones">
          <div className="acad-ng-shell">
            <header className="acad-ng-section-head">
              <span className="acad-ng-eyebrow">Cada etapa importa</span>
              <div>
                <h2>Una sola visión.<br />Tres generaciones.</h2>
                <p>
                  Caminamos con cada persona desde sus primeros pasos de fe hasta una vida de
                  liderazgo, misión y multiplicación.
                </p>
              </div>
            </header>

            <div className="acad-ng-generation-list">
              {generations.map((generation, index) => (
                <article className="acad-ng-generation" key={generation.number}>
                  <div className="acad-ng-generation-media">
                    <img
                      data-acad-edit-id={`nextgen-generation-${generation.number}`}
                      data-acad-edit-label={`Tarjeta: ${generation.title}`}
                      src={generation.image}
                      alt={generation.alt}
                      loading="lazy"
                      decoding="async"
                    />
                    <span>{generation.number}</span>
                  </div>
                  <div className="acad-ng-generation-copy">
                    <small>{generation.kicker}</small>
                    <h3>{generation.title}</h3>
                    <p>{generation.description}</p>
                    <a href="#enfoque">
                      Ver nuestro enfoque <ArrowUpRight aria-hidden="true" />
                    </a>
                  </div>
                  <span className="acad-ng-generation-index" aria-hidden="true">
                    0{index + 1}
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="acad-ng-continuity" data-nav="dark">
          <div className="acad-ng-shell acad-ng-continuity-grid">
            <div className="acad-ng-continuity-media">
              <img
                data-acad-edit-id="nextgen-continuity"
                data-acad-edit-label="Sección: Continuidad con propósito"
                src={assetUrl("children_faith_case_parent.png")}
                alt="Adulto acompañando el crecimiento de fe de una niña"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-ng-continuity-stamp">Fe que permanece</span>
            </div>
            <div className="acad-ng-continuity-copy">
              <span className="acad-ng-eyebrow acad-ng-eyebrow--light">Continuidad con propósito</span>
              <h2>No preparamos solamente el futuro. Activamos la iglesia de hoy.</h2>
              <p>
                Cuando una comunidad escucha, acompaña y da espacio a las nuevas generaciones,
                rejuvenece su misión. Cada niño y cada joven puede conocer a Jesús, servir desde
                ahora y participar en la transformación de su entorno.
              </p>
              <ul>
                <li><UsersRound aria-hidden="true" /> Relaciones intergeneracionales</li>
                <li><Church aria-hidden="true" /> Comunidades saludables y abiertas</li>
                <li><Sparkles aria-hidden="true" /> Liderazgo joven en acción</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="acad-ng-focus" id="enfoque">
          <div className="acad-ng-shell">
            <header className="acad-ng-section-head">
              <span className="acad-ng-eyebrow">Enfoque práctico</span>
              <div>
                <h2>Estar cerca.<br />Formar. Movilizar.</h2>
                <p>
                  Nuestra estrategia se convierte en relaciones, herramientas y oportunidades
                  para poner la fe en acción.
                </p>
              </div>
            </header>

            <div className="acad-ng-focus-grid">
              {focus.map((item) => {
                const Icon = item.icon;

                return (
                  <article className="acad-ng-focus-card" key={item.number}>
                    <span className="acad-ng-focus-number">{item.number}</span>
                    <i><Icon aria-hidden="true" /></i>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-ng-keys">
          <div className="acad-ng-shell">
            <span className="acad-ng-eyebrow acad-ng-eyebrow--light">Palabras que nos mueven</span>
            <div className="acad-ng-keys-grid">
              {keywords.map((keyword) => {
                const Icon = keyword.icon;

                return (
                  <article key={keyword.title}>
                    <i><Icon aria-hidden="true" /></i>
                    <h3>{keyword.title}</h3>
                    <p>{keyword.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-ng-cta">
          <div className="acad-ng-shell acad-ng-cta-inner">
            <div>
              <span className="acad-ng-eyebrow">El mensaje permanece</span>
              <h2>Construyamos una iglesia donde cada generación tenga un lugar, una voz y una misión.</h2>
            </div>
            <a href={homeUrl("contacto/?tema=ministerios#contacto-formulario")}>
              Conectar con Nuevas Generaciones <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
